/* eslint-disable react-hooks/exhaustive-deps */
import { IconDownload, IconEye, IconPlus } from "@tabler/icons-react";
import ColumnsFilter from "../../../components/common/ColumnsFilter";
import DropdownFilter from "../../../components/common/DropdownFilter";
import ListView from "../../../components/common/ListView";
import NormalSearch from "../../../components/common/NormalSearch";
import { useNavigate } from "react-router-dom";
import { useFetchPickupHistoryData } from "../../../apis/pickupApis";
import formatDate, { formatDateTime, formatDateAndTime } from "../../../utilities/helper";
import { useEffect, useState } from "react";
import useDebounce from "../../../utilities/useDebounce";
import Pagination from "../../../components/common/Pagination";
import toast from "react-hot-toast";
import PickupRequestModal from "./PickupRequestModal";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { handleDownload } from "../../../utilities/downloadHelper";

type RequestData = {
  id: string;
};

export default function RequestsTab() {
  const navigate = useNavigate();
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allSelectedData, setAllSelectedData] = useState<RequestData[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false);

  const [view, setView] = useState(false);
  const {
    data: pickupRequestsData,
    refetch: requestRefetch,
    isFetching,
    isLoading,
  } = useFetchPickupHistoryData(pageNo, limit, searchFor);

  const debouncedSearchFor = useDebounce(searchFor, 550);
  useEffect(() => {
    requestRefetch();
  }, [debouncedSearchFor]);

  const handlePaginationFetch = async (page: number) => {
    setPageNo(page);
    try {
      await requestRefetch();
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    }
  };

  useEffect(() => {
    if (pickupRequestsData?.pickupRequests) {
      setAllSelectedData((prev) => {
        const filteredPrev = prev.filter((item) =>
          selectedRows.includes(item.id),
        );
        const newSelected = pickupRequestsData?.pickupRequests.filter(
          (s: any) =>
            selectedRows.includes(s.id) &&
            !filteredPrev.some((item) => item.id === s.id),
        );
        return [...filteredPrev, ...newSelected];
      });
    }
  }, [pickupRequestsData?.pickupRequests, selectedRows]);

  const [columnsData, setColumnsData] = useState([
    { text: "Select", value: "select", isShow: showCheckboxes },
    { text: "S/N", value: "sn", isShow: true },
    { text: "Pickup ID", value: "pickupId", isShow: true },
    { text: "Address", value: "fullAddress", isShow: true },
    { text: "Shipments", value: "shipments", isShow: true },
    { text: "Request Type", value: "requestType", isShow: true },
    { text: "Driver Name", value: "driverName", isShow: true },
    { text: "Create at Date", value: "createAt", isShow: true },
    { text: "Pickup Date & Time", value: "pickupDateTime", isShow: true },
    { text: "Status", value: "status", isShow: true },
    { text: "Actions", value: "actions", isShow: true },
  ]);
  const setHeadings = (value: string | undefined, checked: boolean) => {
    setColumnsData((prev) =>
      prev.map((col) =>
        col.value === value ? { ...col, isShow: checked } : col,
      ),
    );
  };

  const handleSelectRow = (id: string) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === pickupRequestsData?.pickupRequests?.length) {
      setSelectedRows((prev) =>
        prev.filter(
          (id) =>
            !pickupRequestsData.pickupRequests.some((s: any) => s.id === id),
        ),
      );
    } else {
      const newSelections =
        pickupRequestsData?.pickupRequests
          ?.filter((s: any) => !selectedRows.includes(s.id))
          .map((s: any) => s.id) || [];
      setSelectedRows((prev) => [...prev, ...newSelections]);
    }
  };

  const columns = columnsData
    .filter((c) => c.isShow)
    .map((col) => {
      switch (col.value) {
        case "select":
          return {
            label: (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    pickupRequestsData?.pickupRequests?.length > 0 &&
                    pickupRequestsData?.pickupRequests?.every((s: any) =>
                      selectedRows.includes(s.id),
                    )
                  }
                  onChange={handleSelectAll}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                                bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
            render: (s: any) => (
              <label className="relative flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedRows.includes(s.id)}
                  onChange={() => handleSelectRow(s.id)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] 
                  bg-white checked:bg-[#FBD037] checked:text-white disabled:cursor-not-allowed"
                />
                <span
                  className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 
                                top-1/2 left-[10px] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                >
                  ✓
                </span>
              </label>
            ),
          };
        case "sn":
          return {
            label: "#",
            render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
          };
        case "pickupId":
          return {
            label: "Pickup ID",
            render: (c: any) => c.pickupId || "",
          };
        case "fullAddress":
          return {
            label: "Address",
            render: (c: any) => (
              <div
                className="w-64 truncate line-clamp-1"
                title={c.fullAddress || ""}
              >
                {c.fullAddress || ""}
              </div>
            ),
          };
        case "shipments":
          return {
            label: "Shipments",
            render: (c: any) => c.shipmentsCount || 0,
          };
        case "requestType":
          return {
            label: "Request Type",
            render: (c: any) => {
              const formattedRequestType =
                c.requestType.charAt(0).toUpperCase() +
                c.requestType.slice(1).toLowerCase();

              return <span>{formattedRequestType || ""}</span>;
            },
          };
        case "driverName":
          return {
            label: "Driver Name",
            render: (c: any) => c.driverName || "Not Assigned",
          };
        case "createdAt":
          return {
            label: "Create at Date ",
            render: (c: any) => formatDate(c.createdAt) || "",
          };
        case "pickupDateTime":
          return {
            label: "Pickup Date & Time",
            render: (c: any) => formatDateAndTime(c.pickupDate, c.timeSlot) || "",
          };
        case "status":
          return {
            label: "Status",
            render: (c: any) => {
              let statusClasses = "";

              const formattedStatus =
                c.status.charAt(0).toUpperCase() +
                c.status.slice(1).toLowerCase();

              if (formattedStatus === "Generated") {
                statusClasses = "border-[#FBD037] bg-[#FBD037]/20 ";
              } else if (formattedStatus === "Assigned") {
                statusClasses = "border-[#0088FF] bg-[#C6DFF7]  ";
              } else if (formattedStatus === "Collected") {
                statusClasses = "border-[#20BF55] bg-[#CCEAD7] ";
              } else if (formattedStatus === "Completed") {
                statusClasses = "border-[#20BF55] bg-[#20BF55]/20 ";
              }

              return (
                <div className="flex items-center justify-center ">
                  <span
                    className={`rounded-full border text-[#212121]  px-4 py-1.5 text-xs font-medium text-center inline-block ${statusClasses}`}
                  >
                    {formattedStatus || ""}
                  </span>
                </div>
              );
            },
          };
        case "actions":
          return {
            label: "Actions",
            render: (c: any) => (
              <div className="relative flex justify-center">
                <button
                  onClick={() => {
                    navigate(`/shipment-details/${c.id}`);
                  }}
                  className="p-2 border rounded-full bg-[#F7F6F2]"
                >
                  <IconEye size={22} />
                </button>
              </div>
            ),
          };

        default:
          return { label: "", render: () => null };
      }
    });

  const mapDataToExport = (c: any, index: number) => ({
    "S/N": index + 1,
    "Pickup ID": c.pickupId,
    "Full Address": c.fullAddress,
    Shipments: c.shipmentsCount || 0,
    "Request Type": c.requestType || "",
    "Driver Name": c.driverName,
    "Create at Date": formatDate(c.createdAt) || "",
    "Pickup Date & Time": formatDateTime(c.pickupDate) || "",
    Status: c.status,
  });

  return (
    <div>
      <div className="flex items-center">
        <div className=" bg-[#F7F6F2] flex items-center justify-between px-6 py-3 w-full rounded-tl-3xl rounded-tr-[40px] ">
          <h1 className="text-xl text-[#212121] tracking-[-0.5px] font-medium">
            Requested Lists
          </h1>
          <div className="flex items-center gap-2">
            {pickupRequestsData &&
              pickupRequestsData.totalPages * limit > 10 && (
                <EntriesSelector
                  value={limit}
                  options={[
                    10,
                    ...(pickupRequestsData.totalPages * limit > 10 ? [20] : []),
                    ...(pickupRequestsData.totalPages * limit > 20 ? [30] : []),
                    ...(pickupRequestsData.totalPages * limit > 50 ? [50] : []),
                    ...(pickupRequestsData.totalPages * limit > 100
                      ? [100]
                      : []),
                  ]}
                  onChange={(val) => {
                    setLimit(val);
                    setPageNo(1);
                  }}
                />
              )}
            <ColumnsFilter
              columnsData={columnsData}
              setHeadings={setHeadings}
            />
            <DropdownFilter filterheadings={[]} />
            <NormalSearch
              value={searchFor}
              onChange={(e) => {
                setSearchFor(e.target.value);
                setPageNo(1);
              }}
            />
          </div>
        </div>
        <div className="relative ml-5">
          <div className=" flex items-center gap-2 justify-end rounded-full">
            <button
              onClick={() =>
                handleDownload({
                  selectedRows,
                  allSelectedData,
                  setSelectedRows,
                  setAllSelectedData,
                  showCheckboxes,
                  setShowCheckboxes,
                  setColumnsData,
                  mapDataToExport,
                  fileName: "requested_list.xlsx",
                })
              }
              className={`flex items-center gap-1 p-2.5 text-sm rounded-full border border-[#FFFFFF] ${
                showCheckboxes && selectedRows.length === 0
                  ? "bg-[#F7F6F2]/50 text-[#404040]/50"
                  : "bg-[#F7F6F2] text-[#404040]"
              }`}
            >
              <IconDownload stroke={1.5} size={24} />
              {selectedRows.length > 0 && <span>({selectedRows.length})</span>}
            </button>
            <button
              onClick={() => setView(true)}
              className="flex items-center gap-1  p-2.5 text-sm rounded-full border border-[#FFFFFF] bg-[#F7F6F2]"
            >
              <IconPlus stroke={1.5} size={24} />
            </button>
          </div>
        </div>
      </div>
      <div className="pb-4">
        {isLoading || isFetching ? (
          <TableSkeleton
            rows={6}
            columns={columnsData.filter((c) => c.isShow).map((c) => c.text)}
          />
        ) : pickupRequestsData?.pickupRequests ? (
          <ListView
            data={pickupRequestsData?.pickupRequests ?? []}
            columns={columns}
          />
        ) : (
          <NoDataFound />
        )}
      </div>

      {pickupRequestsData?.totalPages > 1 && (
        <div className="flex justify-center items-center">
          {" "}
          <Pagination
            totalPages={pickupRequestsData?.totalPages}
            fetchData={handlePaginationFetch}
            currentPage={pageNo}
          />
        </div>
      )}

      <PickupRequestModal
        open={view}
        onClose={() => setView(false)}
        refetchRequests={requestRefetch}
      />
    </div>
  );
}
